import { useRef, useMemo } from 'react';
import { atom, reatomAsync, withAbort, withErrorAtom } from '@reatom/framework';
import { useAction, useAtom } from '@reatom/npm-react';
import { z } from 'zod';
import { isApiError } from '@/js/libs/api/index.ts';
import { getComponentDebugName } from '@/js/libs/reatom-devtools/index.ts';
import { TOP_CTX_REF } from './constants.js';
import { parseError } from './parseError.js';
const useLazyRequestLegacy = ({ apiMethod, cancelPreviousRequests = true, mapData = (data) => data, onFail = () => { }, onSuccess = () => { }, subscriptions = {}, }) => {
    const isMetaNeeded = subscriptions.all ?? true;
    const isErrorNeeded = subscriptions.error ?? isMetaNeeded;
    const isPendingNeeded = subscriptions.pending ?? isMetaNeeded;
    const isStatusNeeded = subscriptions.status ?? isMetaNeeded;
    const configRef = useRef({
        cancelPreviousRequests,
        mapData,
        onFail,
        onSuccess,
    });
    configRef.current = {
        cancelPreviousRequests,
        mapData,
        onFail,
        onSuccess,
    };
    const name = getComponentDebugName(`${apiMethod.name}Lazy`);
    const model = useMemo(() => {
        const statusAtom = atom(undefined, `${name}._statusAtom`);
        const fetchAction = reatomAsync(async (ctx, ...params) => {
            const cfg = configRef.current;
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const allParams = {};
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { paramsOrder } = apiMethod;
            for (let i = 0; i < paramsOrder.length; i += 1) {
                switch (paramsOrder[i].type) {
                    case 'body':
                        allParams[paramsOrder[i].paramName] = params[i];
                        break;
                    case 'formData':
                        for (const [k, v] of params[i].entries()) {
                            allParams[k] = v;
                        }
                        break;
                    case 'parameters':
                        Object.assign(allParams, params[i]);
                        break;
                    default:
                        throw new Error('no defined params place');
                }
            }
            const response = await apiMethod({ params: allParams, signal: ctx.controller.signal });
            // fix for the backward compatibility
            response.data = response.data.data;
            if (typeof response.data === 'object') {
                response.data.adnz_response_code = response.status;
            }
            // call `mapData` and `onSuccess` here to handle errors in the catch.
            const mappedData = cfg.mapData(response.data);
            try {
                TOP_CTX_REF.ctx = ctx;
                // @ts-expect-error cannot infer type
                cfg.onSuccess(response.data, params, response.status);
            }
            finally {
                TOP_CTX_REF.ctx = null;
            }
            return Object.assign(response, { mappedData });
        }, {
            name,
            onEffect: (ctx) => statusAtom(ctx, undefined),
            onFulfill: (ctx, { status }) => statusAtom(ctx, status),
            onReject: (ctx, error) => statusAtom(ctx, isApiError(error, z.any()) ? error.status : undefined),
        }).pipe(withAbort({ strategy: configRef.current.cancelPreviousRequests ? 'last-in-win' : 'none' }), withErrorAtom((ctx, e) => {
            const error = parseError(e, name);
            // safety call `onFail` before render
            if (error)
                ctx.schedule(() => configRef.current.onFail(error));
            return error;
        }));
        const isLoadingAtom = atom((ctx) => ctx.spy(fetchAction.pendingAtom) > 0, `${name}._isLoadingAtom`);
        const metaAtom = atom((ctx) => ({
            pending: isPendingNeeded ? ctx.spy(isLoadingAtom) : ctx.get(isLoadingAtom),
            error: isErrorNeeded ? ctx.spy(fetchAction.errorAtom) : ctx.get(fetchAction.errorAtom),
            status: isStatusNeeded ? ctx.spy(statusAtom) : ctx.get(statusAtom),
        }), `${name}._metaAtom`);
        return {
            ...fetchAction,
            statusAtom,
            metaAtom,
            fetch: (...params) => fetchAction(...params).then((response) => response.mappedData),
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiMethod]);
    const [meta] = useAtom(model.metaAtom);
    // `useAction` to track component name in logs
    const fetch = useAction((ctx, ...params) => {
        const promise = model.fetch(TOP_CTX_REF.ctx ?? ctx, ...params);
        return promise;
    });
    return [fetch, meta];
};
export default useLazyRequestLegacy;
